/**
 * The sentence the SERVER wrote, out of THIS transport's envelope.
 *
 * ── why it lives here (#1621) ───────────────────────────────────────────────
 * `transport.ts` is what builds the envelope:
 *
 *     throw new SolumTransportError(res.status, res.statusText, body, cfg.url)
 *
 * so parsing it back apart is the SDK's own business, not an application's. It
 * was written in `apps/portal/src/data.tsx` because the portal was the first
 * host that needed it; it is MOVED here rather than copied into the second host
 * (ui-and-experience §1 — copying is the default unless somebody stops it), and
 * the portal now imports this. The behaviour is unchanged: this is the portal's
 * live body, extracted, and `apps/portal` re-exports the same name so no call
 * site there moved.
 *
 * ── what it is for ──────────────────────────────────────────────────────────
 * Governed doors write their refusals for the person reading them.
 * `core.activate_flow` refuses with
 *
 *     "<flow> requires a green simulation before it goes live — the definition
 *      CHANGED since the last simulation …"
 *
 * and puts the actionable half in the **hint**:
 *
 *     "run core.simulate_flow(flow, subject_type, subject_id), review the trace
 *      (core.describe_simulation), then activate"
 *
 * `String((error as Error).message)` prints the whole envelope instead —
 * `solum sdk: 403 Forbidden — {"code":"42501","message":"…","hint":null}` — raw
 * JSON, a SQLSTATE and the transport's own name, and it drops the hint entirely.
 * A refusal a person cannot act on is a refusal that did not arrive.
 *
 * The `DB_PROSE` guard is what keeps this safe to point at ANY door: a message
 * that reads like Postgres talking to a developer ("permission denied for
 * relation…", "null value in column…") is for the log, and the caller's own
 * fallback sentence is used instead. The status is kept either way, because it
 * is the one thing support will ask for.
 */
import { SolumTransportError } from './transport.js'

/**
 * Postgres prose. A message matching this is for the log, never for a person.
 * Kept deliberately narrow: the point is to catch a leak, not to second-guess
 * the sentences a door writes on purpose.
 */
const DB_PROSE = /permission denied|relation |column |schema |function [a-z_.]+\(|syntax error|violates|null value/i

/**
 * @param e        whatever the transport threw.
 * @param fallback the caller's own sentence, given the HTTP status when one is
 *                 recoverable — used when the door wrote nothing a person
 *                 should read.
 */
export function serverRefusal(e: unknown, fallback: (status: string | undefined) => string): string {
  const raw = e instanceof Error ? e.message : ''
  /* The transport now CARRIES what this used to reconstruct (#1790). The regexes
     stay as the fallback because this function is also handed errors the
     transport did not throw — a PGlite rig's, a caller's own — and a structured
     reader that silently returned nothing for those would be worse than the
     archaeology it replaces. */
  const structured = e instanceof SolumTransportError ? e : null
  const status = structured ? String(structured.status) : /\b(\d{3})\b/.exec(raw)?.[1]
  const payload = structured
    ? /^\s*(\{[\s\S]*\})\s*$/.exec(structured.payload)?.[1]
    : /—\s*(\{[\s\S]*\})\s*$/.exec(raw)?.[1]
  if (payload) {
    try {
      const parsed = JSON.parse(payload) as { message?: unknown; hint?: unknown }
      const message = parsed.message
      // Shared assurance doors return a stable machine outcome. Both hosts
      // need the human next step, never the internal provider/action hint.
      if (message === 'step_up_required')
        return 'Verify your session with your authenticator, then retry this action.'
      if (typeof message === 'string' && message.trim() && !DB_PROSE.test(message)) {
        /* The HINT too, where the door wrote one. `activate_flow`'s hint is the
           review's to-do list — "run core.simulate_flow(…), review the trace,
           then activate" — and dropping it leaves a person correctly informed
           and with nothing to do. */
        const hint = typeof parsed.hint === 'string' && parsed.hint.trim() && !DB_PROSE.test(parsed.hint)
          ? ` ${parsed.hint.trim()}`
          : ''
        return `${message}${hint}`
      }
    } catch { /* not JSON after all — fall through to the plain sentence */ }
  }
  return fallback(status)
}

/** What a person reads for a status the server gave no sentence for. */
const STATUS_SENTENCE: Record<string, string> = {
  '401': 'Your session has ended. Sign in again.',
  '403': 'You don’t have access to this.',
  '404': 'That could not be found.',
  '409': 'That changed in the meantime. Reload and try again.',
}

/**
 * Any error, as a sentence a person may read (#3012).
 *
 * The UI/UX review found the raw transport envelope on screen — on a record's
 * related sections, `solum sdk: 403 — {"code":"42501", … "hint":"Grant the
 * required privileges … GRANT SELECT ON <schema.table> …"}`. About 170 places
 * rendered `error.message` directly. This is the one function they call
 * instead, so the rule lives in one place:
 *
 *   · a transport error → the door's own sentence (+ hint) when it wrote one
 *     a person may read (`serverRefusal`), else a sentence for its status,
 *     else `fallback`;
 *   · an error the APP threw with a human message → that message, unchanged —
 *     those are sentences written for this screen;
 *   · anything that reads like Postgres or JSON → `fallback`.
 *
 * `error.message` itself stays the machine envelope: QueryBoundary,
 * writeState, classifyToolFailure and the installer classify by it.
 */
export function readableError(e: unknown, fallback = 'That could not be completed. Try again.'): string {
  const raw = e instanceof Error ? e.message : typeof e === 'string' ? e : ''
  const transport = e instanceof SolumTransportError || /^solum sdk:\s*\d{3}\b/.test(raw)
  if (transport) {
    return serverRefusal(e, (status) => {
      if (status && /^5\d\d$/.test(status)) return 'The server could not complete this. Try again in a moment.'
      return (status && STATUS_SENTENCE[status]) || fallback
    })
  }
  const text = raw.trim()
  if (!text || /^solum sdk/i.test(text) || /^[{[]/.test(text) || DB_PROSE.test(text)) return fallback
  return text
}
